import { useEffect, useMemo } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import { useGetTerminalsForLocation } from '@ordr-as/customer-onboarding-service-types/epos';
import type { InhouseTerminalDto } from '@ordr-as/customer-onboarding-service-types/epos';
import { useGetLocationInfo, useGetLocationsForCurrentUserV2 } from '@ordr-as/ordr-service-types/epos';
import type { LocationInfoDto } from '@ordr-as/ordr-service-types/epos';
import { FavritApp, useGetPaymentMethodsForLocation } from '@ordr-as/payment-service-types/epos';
import type { LocationPaymentMethodDto } from '@ordr-as/payment-service-types/epos';
import type { PaymentMethodDto } from '../../generated-api';
import { isValidLocationId } from '../utils/locationId';

const LOCATION_ID_CACHE_KEY = 'favrit.epos.locationIdMapping';

function getCachedNumericId(locationId: string): number | undefined {
  try {
    const cached = localStorage.getItem(`${LOCATION_ID_CACHE_KEY}.${locationId}`);
    if (!cached) {
      return undefined;
    }
    const parsed = Number.parseInt(cached, 10);
    // Validate: must be a positive integer (handles NaN, 0, negative)
    return parsed > 0 ? parsed : undefined;
  } catch {
    return undefined;
  }
}

function setCachedNumericId(locationId: string, numericId: number) {
  try {
    localStorage.setItem(`${LOCATION_ID_CACHE_KEY}.${locationId}`, numericId.toString());
  } catch {
    // Ignore storage errors
  }
}

export function useCachedLocationId(locationId?: string): number | undefined {
  return locationId ? getCachedNumericId(locationId) : undefined;
}

export function useQueryLocations() {
  const { queryKey: _queryKey, ...result } = useGetLocationsForCurrentUserV2({
    query: {
      staleTime: 10 * 60 * 1000,
    },
  });
  return result;
}

const LOCATION_META = { persist: true } as const;

/** Resolves a string locationId to its numeric id. */
export const useQueryLocation = (id?: string): { numericId: number | undefined; isError: boolean } => {
  const cachedNumericId = useCachedLocationId(id);

  const { data: locationsList, isError } = useGetLocationsForCurrentUserV2({
    query: { staleTime: 10 * 60 * 1000 },
  });

  const numericId = useMemo(() => {
    if (!id) return undefined;
    if (cachedNumericId) return cachedNumericId;
    return locationsList?.find((loc) => loc.locationId === id)?.id;
  }, [id, cachedNumericId, locationsList]);

  useEffect(() => {
    if (id && numericId && !cachedNumericId) {
      setCachedNumericId(id, numericId);
    }
  }, [id, numericId, cachedNumericId]);

  return { numericId, isError: isError && !cachedNumericId };
};

/** Fetches core location data from ordr-service. */
export const useQueryLocationInfo = (
  numericId: number | undefined
): UseQueryResult<LocationInfoDto | undefined, Error | null> =>
  useGetLocationInfo(numericId ?? 0, {
    query: {
      enabled: isValidLocationId(numericId?.toString()) && numericId !== undefined,
      meta: LOCATION_META,
      refetchOnMount: true,
    },
  });

const INTEGRATED_TERMINAL_METHOD = 'INTEGRATED_TERMINAL';
const UNINTEGRATED_PROVIDER = 'UNINTEGRATED';
const SUPPORTED_UNINTEGRATED_FIELD_TYPES = new Set(['TEXT_SINGLE_LINE', 'TEXT_COMBO_BOX']);
const MAX_UNINTEGRATED_FIELDS = 4;

const titleCasePaymentMethod = (paymentMethod: string): string => {
  const spaced = paymentMethod.split('_').join(' ').toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

const isEposLocationPaymentMethod = (m: LocationPaymentMethodDto): boolean =>
  m.enabledInApps.some((app) => app.includes('EPOS')) && m.configuration.visibleInApps;

const sanitizeUnintegratedFields = (
  schema: LocationPaymentMethodDto['configuration']['overridableConfiguration']['unintegratedFieldsSchema']
) =>
  schema
    ?.slice(0, MAX_UNINTEGRATED_FIELDS)
    .filter((s) => SUPPORTED_UNINTEGRATED_FIELD_TYPES.has(s.type))
    .map((s) => ({
      name: s.name,
      type: s.type,
      required: s.required,
      displayLabel: s.displayLabel,
    }));

const toUnintegratedPaymentMethod = (m: LocationPaymentMethodDto): PaymentMethodDto => ({
  paymentMethod: m.paymentMethod,
  paymentMethodCategory: m.paymentMethodCategory,
  id: `${m.paymentProvider} - ${m.paymentMethod}`,
  provider: m.paymentProvider,
  label: m.configuration.overridableConfiguration.paymentMethodDisplayLabel ?? titleCasePaymentMethod(m.paymentMethod),
  isNewUnintegratedPaymentMethod: true,
  isDirectSalesMethod: m.enabledInApps.includes(FavritApp.EPOS_APP_DIRECT_SALE),
  isOpenBillMethod: m.enabledInApps.includes(FavritApp.EPOS_APP_OPEN_BILL),
  unintegratedPaymentFields: sanitizeUnintegratedFields(
    m.configuration.overridableConfiguration.unintegratedFieldsSchema
  ),
  sendNotification: false,
  isUsingHub: false,
  askForTip: false,
});

const toTerminalPaymentMethod = (
  terminal: InhouseTerminalDto,
  integratedTerminalEntry: LocationPaymentMethodDto | undefined
): PaymentMethodDto => ({
  paymentMethod: terminal.paymentMethod,
  paymentMethodCategory: integratedTerminalEntry?.paymentMethodCategory ?? 'UNCLASSIFIED',
  id: terminal.terminalId,
  provider: terminal.paymentProvider,
  label: terminal.terminalName,
  isNewUnintegratedPaymentMethod: false,
  isDirectSalesMethod: integratedTerminalEntry?.enabledInApps.includes(FavritApp.EPOS_APP_DIRECT_SALE) ?? false,
  isOpenBillMethod: integratedTerminalEntry?.enabledInApps.includes(FavritApp.EPOS_APP_OPEN_BILL) ?? false,
  sendNotification: false,
  isUsingHub: terminal.isUsingHub,
  askForTip: terminal.askForTip,
});

/**
 * Fetches payment methods for a location by combining payment-service (unintegrated methods)
 * with customer-onboarding-service (integrated terminals).
 */
export const useQueryLocationPaymentMethods = (numericId: number | undefined) => {
  const enabled = numericId !== undefined;
  const queryOptions = { enabled, meta: LOCATION_META, refetchOnMount: true } as const;

  const paymentMethodsQuery = useGetPaymentMethodsForLocation(numericId ?? 0, { query: queryOptions });
  const terminalsQuery = useGetTerminalsForLocation({ locationId: numericId ?? 0 }, { query: queryOptions });

  const isPending = paymentMethodsQuery.isPending || terminalsQuery.isPending;

  const data = useMemo(() => {
    if (isPending) return undefined;

    const eposPaymentMethods = (paymentMethodsQuery.data ?? []).filter(isEposLocationPaymentMethod);
    const terminalsList = terminalsQuery.data ?? [];

    const integratedTerminalEntry = eposPaymentMethods.find((m) => m.paymentMethod === INTEGRATED_TERMINAL_METHOD);
    const hasIntegratedTerminals =
      integratedTerminalEntry?.enabledInApps.includes(FavritApp.EPOS_APP_DIRECT_SALE) === true ||
      integratedTerminalEntry?.enabledInApps.includes(FavritApp.EPOS_APP_OPEN_BILL) === true;

    const unintegrated = eposPaymentMethods
      .filter((m) => m.paymentProvider === UNINTEGRATED_PROVIDER)
      .map(toUnintegratedPaymentMethod);

    const terminals = hasIntegratedTerminals
      ? terminalsList.map((t) => toTerminalPaymentMethod(t, integratedTerminalEntry))
      : [];

    return { paymentMethods: [...terminals, ...unintegrated] };
  }, [isPending, paymentMethodsQuery.data, terminalsQuery.data]);

  return {
    data,
    isPending,
    isError: paymentMethodsQuery.isError && terminalsQuery.isError,
  };
};
